import React, { useState, useEffect, useCallback, useRef } from "react";
import RequestSideBar from "./sidebar";
import { ShortcutsDialog, useRequestShortcuts } from "./shortcuts";
import RequestMobileNav from "./mobile-nav";
import RequestMain from "./main";
import MyForms from "./my-forms";
import buildhubBg from "../../images/bg.png";
import { Link, useLocation } from "react-router-dom";
import FormSelectModal from "./reusable/modal";
import RequestNew from "./request-new";
import showSwal from "../swal";
import ApiService from "../../api/apiService";
import { common } from "../../util/common";
import RequestDetail from "./view-request-detail";
import ApproversModal from "./reusable/approvers-modal";
import ApprovalRequests from "./approval-requests";
import RequestsHistory from "./requests-history";
import Dashboard from "./dashboard";
import ForTagging from "./for-tagging";
import ApprovalHistory from "./approval-history";
import ReportList from "./report-list";
import CCARRequestDetail from "./ccar-request-detail";
import ForCCAR from "./for-ccar";
import {
  DEFAULT_PAGE,
  getNavItem,
  getPageTitle,
  hasPagePermission,
  isKnownPage,
  pageHref,
} from "./nav-config";

function RequestIndex({ userDetails }) {
  const [showModal, setShowModal] = useState(false);
  const [showApproversModal, setShowApproversModal] = useState(false);
  const [availableForms, setAvailableForms] = useState([]);
  const [formsLoaded, setFormsLoaded] = useState(false);
  const [approversData, setApproversData] = useState(null);
  const [pagePermissions, setPagePermissions] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const location = useLocation();
  const mainRef = useRef(null);

  useEffect(() => {
    const getForms = async () => {
      try {
        const myForms = await ApiService.forms.getMyForms();
        const { success, forms } = myForms || {};
        if (!success) return showSwal(common.errors.FETCH_MY_FORMS_ERROR);
        setAvailableForms(forms || []);
      } catch {
        showSwal(common.errors.FETCH_MY_FORMS_ERROR);
      } finally {
        // Ends the picker's loading state even when the request fails.
        setFormsLoaded(true);
      }
    };

    const getPagePermissions = async () => {
      const pagePermissions = await ApiService.forms.getPagePermissions();
      const { success, permissions } = pagePermissions || {};

      setPermissionsLoaded(true);
      if (!success) return showSwal(common.errors.PAGE_PERMISSIONS_ERROR);
      setPagePermissions(permissions);
    };

    getForms();
    getPagePermissions();
  }, []);

  const handleShowingModal = useCallback(() => {
    setShowModal((prevState) => !prevState);
  }, []);

  const handleShowApproversModal = () => {
    setShowApproversModal((prevState) => !prevState);
  };

  const showShortcutsDialog = useCallback(() => setShowShortcuts(true), []);
  const hideShortcutsDialog = useCallback(() => setShowShortcuts(false), []);

  const searchParams = new URLSearchParams(location.search);
  const viewingPage = searchParams.get("p") || DEFAULT_PAGE;
  const requestType = searchParams.get("t");

  useRequestShortcuts({
    pagePermissions,
    enabled: !showShortcuts,
    onNewRequest: handleShowingModal,
    onShowHelp: showShortcutsDialog,
  });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${getPageTitle(viewingPage)} | BuildHub Chat`;
    return () => {
      document.title = previousTitle;
    };
  }, [viewingPage]);

  // Pages now switch without a reload, so move focus to the new content
  // (skipped on first load so the browser's default focus is left alone).
  const isFirstPageRef = useRef(true);
  useEffect(() => {
    if (isFirstPageRef.current) {
      isFirstPageRef.current = false;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
  }, [viewingPage]);

  const renderPage = () => {
    if (!isKnownPage(viewingPage)) {
      return (
        <PageState
          title="Page not found"
          message="This link doesn't match any page in Requests."
        />
      );
    }

    if (getNavItem(viewingPage)?.perm && !permissionsLoaded) {
      return <PageState loading />;
    }

    if (!hasPagePermission(viewingPage, pagePermissions)) {
      return (
        <PageState
          title="You don't have access to this page"
          message="Ask your administrator if you think you should."
        />
      );
    }

    switch (viewingPage) {
      case "view-requests":
        return <RequestMain handleShowingModal={handleShowingModal} />;
      case "view-forms":
        return <MyForms />;
      case "dashboard":
        return <Dashboard />;
      case "new-request":
        return <RequestNew />;
      case "view-request-detail": {
        const DetailPage =
          requestType?.toLowerCase() === "ccar"
            ? CCARRequestDetail
            : RequestDetail;
        return (
          <DetailPage
            handleShowApproversModal={handleShowApproversModal}
            setApproversData={setApproversData}
            userDetails={userDetails}
          />
        );
      }
      case "view-approval-list":
        return <ApprovalRequests />;
      case "view-approve-history":
        return <ApprovalHistory />;
      case "view-for-tagging":
        return <ForTagging />;
      case "view-approval-history":
        return <RequestsHistory />;
      case "view-reports":
        return <ReportList />;
      case "view-ccar":
        return <ForCCAR />;
      default:
        return null;
    }
  };

  return (
    <div className="requests-index">
      {showModal && (
        <FormSelectModal
          modalTitle={"New request"}
          handleShowingModal={handleShowingModal}
          formOptions={availableForms}
          isLoading={!formsLoaded}
        />
      )}
      {showApproversModal && (
        <ApproversModal
          handleShowApproversModal={handleShowApproversModal}
          approversData={approversData}
        />
      )}
      {showShortcuts && (
        <ShortcutsDialog
          pagePermissions={pagePermissions}
          onClose={hideShortcutsDialog}
        />
      )}
      <a
        className="requests-skip-link"
        href="#requests-content"
        onClick={(e) => {
          e.preventDefault();
          mainRef.current?.focus();
        }}
      >
        Skip to content
      </a>
      <RequestSideBar
        pagePermissions={pagePermissions}
        permissionsLoaded={permissionsLoaded}
        viewingPage={viewingPage}
        onNewRequest={handleShowingModal}
        onShowShortcuts={showShortcutsDialog}
      />
      <RequestMobileNav
        pagePermissions={pagePermissions}
        permissionsLoaded={permissionsLoaded}
        viewingPage={viewingPage}
        onNewRequest={handleShowingModal}
      />
      <main
        ref={mainRef}
        id="requests-content"
        tabIndex={-1}
        className="requests-main"
        style={{ backgroundImage: `url(${buildhubBg})` }}
      >
        {renderPage()}
      </main>
    </div>
  );
}

function PageState({ loading, title, message }) {
  if (loading) {
    return (
      <div className="requests-page-state" role="status">
        <span className="requests-page-state__spinner" aria-hidden="true" />
        <span className="u-visually-hidden">Loading…</span>
      </div>
    );
  }

  return (
    <div className="requests-page-state">
      <p className="requests-page-state__title">{title}</p>
      <p className="requests-page-state__message">{message}</p>
      <Link className="requests-page-state__link" to={pageHref(DEFAULT_PAGE)}>
        Go to My Requests
      </Link>
    </div>
  );
}

export default RequestIndex;
